import { useMutation } from "@tanstack/react-query";
import { Wrench } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";

type Props = {
  // Human-readable labels of the starter seeds that failed during signup.
  // Null/empty means setup is complete and the banner renders nothing.
  failedSeeds: string[] | null | undefined;
};

type FinishSetupResult = {
  success: boolean;
  failedSeeds: string[];
};

// Task #3606 — Host-facing "finish setting up your account" banner. When a new
// host's starter content (booking template, account defaults, furnishing
// catalog) failed to seed during verify-signup, this offers a one-click,
// idempotent re-run via POST /api/app/account/finish-setup. The seeds are
// idempotent, so re-running is always safe; a successful run clears the
// server-side flag and the banner disappears on the next /api/app/auth/me
// refresh.
export function FinishSetupBanner({ failedSeeds }: Props) {
  const finishSetup = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/account/finish-setup", {});
      return (await res.json()) as FinishSetupResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
    },
  });

  if (!failedSeeds || failedSeeds.length === 0) return null;

  const stillFailing = finishSetup.data && !finishSetup.data.success;

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 text-amber-900"
      data-testid="banner-finish-setup"
    >
      <div className="px-4 py-2.5 flex flex-wrap items-center gap-3">
        <Wrench className="h-4 w-4 shrink-0 text-amber-700" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold">Your account setup didn't fully finish.</span>{" "}
          <span className="text-amber-800" data-testid="text-finish-setup-detail">
            {stillFailing
              ? "We couldn't finish setting up your account just now. Please try again in a moment."
              : "Some starter content didn't set up when you signed up. Finish it now to get going."}
          </span>
        </div>
        <button
          type="button"
          onClick={() => finishSetup.mutate()}
          disabled={finishSetup.isPending}
          className="text-sm font-medium px-3 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 disabled:opacity-50"
          data-testid="button-finish-setup"
        >
          {finishSetup.isPending ? "Finishing..." : stillFailing ? "Try again" : "Finish setup"}
        </button>
      </div>
    </div>
  );
}
